import type { ColumnSizingState } from '@tanstack/react-table'
import { CHART_CARD_TABLE_LAYOUT } from '../../../constants'
import type { ChartDataRow } from '../../../types'
import type { ChartCardTableColumnDef } from '../tanstack'
import { calculateTotalHorizontalPadding, getColumnId } from '.'

export const getColumnsInitialSizing = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
): ColumnSizingState => {
  const sizing: ColumnSizingState = {}

  const padding = CHART_CARD_TABLE_LAYOUT.padding?.head
  const paddingChanges = { right: -5 }

  for (const column of columns) {
    const id = getColumnId(column)
    const { size, meta } = column
    if (size == null) {
      continue
    }

    const isFixedSize = meta?.chartCard?.fixedSize === true
    sizing[id] = size + calculateTotalHorizontalPadding(padding, isFixedSize ? undefined : paddingChanges)
  }

  return sizing
}

/** Matches TanStack Table's default column size when neither state nor column def provides one. */
const FALLBACK_COLUMN_SIZE = 150

interface ColumnFillSizeEntry {
  id: string
  naturalSize: number
  maxSize: number
  isFixed: boolean
}

const resolveColumnFillSizeEntry = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>,
  sizing: ColumnSizingState
): ColumnFillSizeEntry => {
  const id = getColumnId(column)
  const padding = CHART_CARD_TABLE_LAYOUT.padding?.head
  const paddingChanges = { right: -5 }
  const isFixed = column.meta?.chartCard?.fixedSize === true
  const totalHorizontalPadding = calculateTotalHorizontalPadding(padding, isFixed ? undefined : paddingChanges)

  const fallbackMin = CHART_CARD_TABLE_LAYOUT.defaultColumn.minSize ?? 48
  const fallbackMax = CHART_CARD_TABLE_LAYOUT.defaultColumn.maxSize ?? Number.MAX_SAFE_INTEGER

  const minFromDef = column.minSize == null ? undefined : column.minSize + totalHorizontalPadding
  const maxFromDef = column.maxSize == null ? undefined : column.maxSize + totalHorizontalPadding
  const sizeFromDef = column.size == null ? undefined : column.size + totalHorizontalPadding

  const fixedSize = sizeFromDef ?? minFromDef ?? fallbackMin
  const maxSize = isFixed ? fixedSize : Math.max(maxFromDef ?? fallbackMax, fixedSize)

  const sizedFromState = sizing[id]
  const naturalSize = typeof sizedFromState === 'number' ? sizedFromState : sizeFromDef ?? FALLBACK_COLUMN_SIZE

  return { id, naturalSize, maxSize, isFixed }
}

const seedFilledSizingFromNatural = (sizing: ColumnSizingState, entries: ColumnFillSizeEntry[]): ColumnSizingState => {
  const result: ColumnSizingState = { ...sizing }
  for (const entry of entries) {
    result[entry.id] = entry.naturalSize
  }
  return result
}

const distributeExtraWidthProportionally = (
  result: ColumnSizingState,
  entries: ColumnFillSizeEntry[],
  extraWidth: number
): void => {
  let remaining = extraWidth

  while (remaining > 0) {
    const growable = entries.filter((entry) => !entry.isFixed && (result[entry.id] ?? 0) < entry.maxSize)
    if (growable.length === 0) {
      return
    }

    const weightSum = growable.reduce((sum, entry) => sum + (result[entry.id] ?? 0), 0)
    let distributed = 0

    for (const entry of growable) {
      const current = result[entry.id] ?? 0
      const share = weightSum === 0 ? remaining / growable.length : (remaining * current) / weightSum
      const add = Math.min(share, entry.maxSize - current)
      if (add <= 0) {
        continue
      }
      result[entry.id] = current + add
      distributed += add
    }

    if (distributed <= 0) {
      return
    }

    remaining -= distributed
    // Guard against floating-point leftovers that never reach zero.
    if (remaining < 0.5) {
      return
    }
  }
}

const snapFilledSizingToWholePixels = (
  result: ColumnSizingState,
  entries: ColumnFillSizeEntry[],
  containerWidth: number
): void => {
  let pixelDrift = containerWidth
  for (const entry of entries) {
    const rounded = Math.round(result[entry.id] ?? entry.naturalSize)
    result[entry.id] = rounded
    pixelDrift -= rounded
  }

  if (pixelDrift === 0) {
    return
  }

  for (const entry of entries) {
    if (entry.isFixed) {
      continue
    }
    const current = result[entry.id] ?? entry.naturalSize
    const next = Math.min(entry.maxSize, Math.max(entry.naturalSize, current + pixelDrift))
    const applied = next - current
    if (applied === 0) {
      continue
    }
    result[entry.id] = next
    pixelDrift -= applied
    if (pixelDrift === 0) {
      return
    }
  }
}

/**
 * Stretches non-fixed columns so their widths fill `containerWidth` when the natural
 * total is smaller. When the natural total already meets or exceeds the container,
 * returns `sizing` unchanged so horizontal scroll is preserved.
 */
export const getFilledColumnSizing = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>,
  sizing: ColumnSizingState,
  containerWidth: number
): ColumnSizingState => {
  if (containerWidth <= 0 || columns.length === 0) {
    return sizing
  }

  const entries = columns.map((column) => resolveColumnFillSizeEntry(column, sizing))
  const naturalTotal = entries.reduce((sum, entry) => sum + entry.naturalSize, 0)

  if (naturalTotal >= containerWidth) {
    return sizing
  }

  const result = seedFilledSizingFromNatural(sizing, entries)
  distributeExtraWidthProportionally(result, entries, containerWidth - naturalTotal)
  snapFilledSizingToWholePixels(result, entries, containerWidth)
  return result
}
